import {ReactElement, CSSProperties, useLayoutEffect, useRef, useState} from "react";
import "./LootCrate.scss";

export type LootCrateTier = "COMMON" | "RARE" | "LEGENDARY" | string;
export type LootCrateState = "closed" | "open" | "play";

export interface LootCrateProps {
    /** Rarity label shown in the chip (e.g. "COMMON"). */
    tier?: LootCrateTier;
    /** Small chip subtitle (e.g. "Standard drop"). */
    sub?: string;
    /** closed = idle float · open = lid held up · play = auto-play opening loop. */
    state?: LootCrateState;
    /** Tier accent / glow color. Defaults to the COMMON blue. */
    g1?: string;
    /** Brushed-metal trim color. */
    g2?: string;
    /** Square render size in px (or any CSS length). */
    size?: number | string;
    /**
     * Reserve the ANIMATING envelope while the crate is still closed. A closed crate fits
     * a 236px footprint, an animating one needs 320px (lid pop + bulge) and shifts down
     * 22px — so a crate that starts closed and later plays visibly shrinks and jumps at
     * the moment it starts. Consumers that will animate the same crate in place should
     * set this so the geometry is identical before and during the animation.
     */
    reserveAnimationSpace?: boolean;
    /** Show the tier chip pill. Off for small icon-style renders (it doesn't scale + would clip). */
    showChip?: boolean;
    className?: string;
}

/**
 * Tier presets matching the LootBox Concepts handoff.
 * COMMON blue · RARE purple · LEGENDARY gold.
 */
export const LOOT_CRATE_TIERS: Record<"COMMON" | "RARE" | "LEGENDARY", { g1: string; g2: string }> = {
    COMMON: {g1: "#38bdf8", g2: "#9aa7b8"},
    RARE: {g1: "#808080", g2: "#9aa7b8"},
    LEGENDARY: {g1: "#ff9d3c", g2: "#c98f34"},
};

/**
 * Opening-animation timeline (keep in sync with the `play` keyframes in LootCrate.scss).
 * Also mirrored by ANIM_SOLO_MS / OPEN_PHASE_MS in LootBoxOpenDrawer.tsx — retime all
 * three together if the 5s play cycle or its burst/hold percentages change.
 * A consuming flow that plays state="play" then reveals the prize should hold the
 * opening step for LOOT_CRATE_OPEN_MS so the lid visibly pops before the reveal.
 */
export const LOOT_CRATE_PLAY_MS = 5000; // one full pump→open→reset cycle
export const LOOT_CRATE_OPEN_MS = 3200; // lid is open by ~62% of the cycle — cue the reveal here

/**
 * AAA gunmetal loot crate — pixel port of the Claude Design LootCrate prototype.
 * Pure CSS 3D; the opening reveal uses a rim-light on the cavity (the original
 * bright center orb was dropped per design review).
 */
function LootCrate(props: LootCrateProps): ReactElement {
    const {
        tier = "COMMON",
        sub = "",
        state = "closed",
        reserveAnimationSpace = false,
        g1 = "#38bdf8",
        g2 = "#9aa7b8",
        size = 320,
        showChip = true,
        className,
    } = props;

    /*
     * Only the tier colors are inline vars; open/play are real state classes
     * (animation-name from a CSS var doesn't reliably bind keyframes in Chrome).
     */
    /*
     * The iso rotation + 940px perspective project the 150px-authored art to a ~207×221px
     * footprint (measured), so scaling against 150 leaves it ~1.5× too big and clips. Scale
     * against the true projected footprint (+ a small margin) so the whole crate fits its
     * box, centered. Clamped to 1 for sizes ≥ the footprint.
     *
     * When the lid animates (play/open) it pops ~58px AND the crate pumps/bulges up to +6%,
     * so animated renders scale against a taller envelope (320) and shift down (--lc-shift)
     * to reserve headroom for the pop — measured so pumps, burst, and the float bob all stay
     * inside the box with margin. Static renders use the tighter footprint (236) to sit
     * larger/crisper in their tiles, centered (no shift).
     */
    const isAnimating = state === "play" || state === "open";
    /* Reserving up front keeps a closed→play transition geometrically stable. */
    const usesAnimationEnvelope = isAnimating || reserveAnimationSpace;
    const CRATE_FOOTPRINT = usesAnimationEnvelope ? 320 : 236;

    /*
     * The fit math needs the crate's rendered pixel width. Numeric `size` gives it directly;
     * a string size ("100%", "12rem", …) does not, so we measure the root once it's laid out
     * (ResizeObserver) and scale against that. Until the first measure lands, `effectiveSize`
     * is null → scale 1 (the pre-fit render) for a frame, then it corrects. The observer is
     * skipped entirely for numeric sizes.
     */
    const rootRef = useRef<HTMLDivElement>(null);
    const [measuredSize, setMeasuredSize] = useState<number | null>(null);
    useLayoutEffect(() => {
        if (typeof size === "number" || !rootRef.current) return undefined;
        const el = rootRef.current;
        const ro = new ResizeObserver(entries => {
            const w = entries[0]?.contentRect.width;
            if (w) setMeasuredSize(w);
        });
        ro.observe(el);
        return () => ro.disconnect();
    }, [size]);

    const effectiveSize = typeof size === "number" ? size : measuredSize;
    const scale = effectiveSize ? Math.min(1, effectiveSize / CRATE_FOOTPRINT) : 1;
    /*
     * Down-shift in the crate's own 150px canvas space — applied inside the scaled scene so
     * it scales with the art — that re-centers the taller open-lid envelope. Measured at 22px.
     * Static renders are already centered → 0.
     */
    const shiftPx = usesAnimationEnvelope ? 22 : 0;
    const rootStyle: Record<string, string> = {
        width: typeof size === "number" ? `${size}px` : size,
        height: typeof size === "number" ? `${size}px` : size,
        "--lc-scale": String(scale),
        "--lc-shift": `${shiftPx}px`,
        "--g1": g1,
        "--g2": g2,
    };

    const stateClass = state === "open" ? " loot-crate--open" : state === "play" ? " loot-crate--play" : "";

    return (
        <div ref={rootRef} className={`loot-crate${stateClass}${className ? ` ${className}` : ""}`} style={rootStyle as CSSProperties}>
            <div className="lc-stage">
                <div className="lc-aura"/>
                <div className="lc-pool"/>
                <div className="lc-shadow"/>

                <div className="lc-scene">
                    <div className="lc-float">
                        <div className="lc-iso">

                            {/* base front */}
                            <div className="lc-base-front">
                                <div className="lc-ridges"/>
                                <div className="lc-panel"/>
                                <div className="lc-rivet lc-rivet--tl"/>
                                <div className="lc-rivet lc-rivet--tr"/>
                                <div className="lc-rivet lc-rivet--bl"/>
                                <div className="lc-rivet lc-rivet--br"/>
                                <div className="lc-gem"/>
                                <div className="lc-core"/>
                            </div>

                            {/* base right */}
                            <div className="lc-base-right">
                                <div className="lc-ridges"/>
                                <div className="lc-panel"/>
                                <div className="lc-rivet lc-rivet--dim lc-rivet--tl"/>
                                <div className="lc-rivet lc-rivet--dim lc-rivet--br"/>
                            </div>

                            {/* cavity + rim-light */}
                            <div className="lc-cavity"/>
                            <div className="lc-cavity-rim"/>

                            {/* lid */}
                            <div className="lc-lid">
                                <div className="lc-lid-front">
                                    <div className="lc-seam-trim"/>
                                    <div className="lc-seam-glow"/>
                                </div>
                                <div className="lc-lid-right">
                                    <div className="lc-seam-trim"/>
                                    <div className="lc-seam-glow"/>
                                </div>
                                <div className="lc-lid-top">
                                    <div className="lc-ridges lc-ridges--v"/>
                                    <div className="lc-inset"/>
                                    <div className="lc-rivet lc-rivet--tl"/>
                                    <div className="lc-rivet lc-rivet--tr"/>
                                    <div className="lc-rivet lc-rivet--bl"/>
                                    <div className="lc-rivet lc-rivet--br"/>
                                    <div className="lc-gem"/>
                                    <div className="lc-core"/>
                                </div>
                            </div>

                        </div>
                    </div>
                </div>

                {/* The box only opens (lid lift + gold rim-lit cavity). The reward
                    reveal is a separate step in the consuming flow (e.g. RedeemDrawer's
                    "revealed" step) — nothing renders inside the cavity here. */}

                {/* tier chip */}
                {showChip &&
                    <div className="lc-chip">
                        <span className="lc-chip-marker"/>
                        <span className="lc-chip-tier">{tier}</span>
                        {sub ? <span className="lc-chip-sub">{sub}</span> : null}
                    </div>
                }
            </div>
        </div>
    );
}

export default LootCrate;
